<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { formatPhoneNumber } from '@Helpers';
import type { FieldSchema } from '../../../types';
import { checkDuplicatePersonService } from '../../../services';
import useMFieldContact from './useMFieldContact';

interface PersonResult {
  _id: { $oid: string };
  addresses: string;
  full_name: string;
  id: string;
}

const props = withDefaults(
  defineProps<{
    error?: string;
    field: FieldSchema;
    placeholder?: string;
    required?: boolean;
  }>(),
  {
    placeholder: 'components.forms.placeholder.common',
  }
);

const { contactData, loadingContactData, personId, showPhoneCard, showEmailCard } = useMFieldContact();

let requestCounter = 0;
let checkTimeoutId: ReturnType<typeof setTimeout> | null = null;
let pendingBlurCheck: 'phone' | 'email' | null = null;
let isSelectingFromModal = false;

const model = defineModel<string | undefined>();
const results = ref<{ phone: PersonResult[]; email: PersonResult[] }>({ phone: [], email: [] });
const isCheckingDuplicate = ref(false);
const lastCheckedValue = ref<string>('');

onMounted(() => resetComponentState());
onUnmounted(() => clearChecksAndPendingRequests());

watch(model, () => checkForPendingBlurCheck());

function checkForPendingBlurCheck() {
  const userIsInteracting = isSelectingFromModal || showPhoneCard.value || showEmailCard.value;
  if (userIsInteracting) return;

  const userIsTyping = pendingBlurCheck && model.value;
  if (userIsTyping) {
    const fieldToCheck = pendingBlurCheck!;
    pendingBlurCheck = null;
    handleDuplicateCheck(fieldToCheck);
  }
}

function clearChecksAndPendingRequests() {
  if (checkTimeoutId) {
    clearTimeout(checkTimeoutId);
    checkTimeoutId = null;
  }

  requestCounter++;
  pendingBlurCheck = null;
}

function resetComponentState() {
  personId.value = undefined;
  isCheckingDuplicate.value = false;
  lastCheckedValue.value = '';
  isSelectingFromModal = false;
  showPhoneCard.value = false;
  showEmailCard.value = false;
  results.value = { phone: [], email: [] };
  pendingBlurCheck = null;
  loadingContactData.value = false;

  if (!checkTimeoutId) return;

  clearTimeout(checkTimeoutId);
  checkTimeoutId = null;
}

function handleBlur(field: 'phone' | 'email') {
  const ongoingCheckOrInteraction =
    isCheckingDuplicate.value || isSelectingFromModal || showPhoneCard.value || showEmailCard.value;
  if (ongoingCheckOrInteraction) return;

  pendingBlurCheck = field;

  // NOTE: This timeout is for handling browser event cycles.
  const blurCheckTimeout = 2;
  setTimeout(() => {
    if (pendingBlurCheck !== field) return;

    pendingBlurCheck = null;
    handleDuplicateCheck(field);
  }, blurCheckTimeout);
}

function handleDuplicateCheck(field: string) {
  const ongoingCheckOrInteraction =
    isCheckingDuplicate.value || isSelectingFromModal || showPhoneCard.value || showEmailCard.value;
  if (ongoingCheckOrInteraction) return;

  if (checkTimeoutId) {
    clearTimeout(checkTimeoutId);
    checkTimeoutId = null;
  }

  checkTimeoutId = performDuplicateCheck(field) as unknown as ReturnType<typeof setTimeout>;
}

function performDuplicateCheck(field: string) {
  const currentValue = model.value?.trim();

  if (!currentValue) return;

  const normalizedValue: string =
    field === 'phone' ? formatPhoneNumber(currentValue) || currentValue : currentValue.toLowerCase();

  if (lastCheckedValue.value === normalizedValue) return;

  requestCounter++;
  const currentRequestId = requestCounter;

  results.value[field as 'phone' | 'email'] = [];
  if (field === 'phone') showPhoneCard.value = false;
  if (field === 'email') showEmailCard.value = false;

  const PAYLOAD = {
    entity: 'people',
    field,
    value: normalizedValue,
  };

  isCheckingDuplicate.value = true;
  lastCheckedValue.value = normalizedValue;

  checkDuplicatePersonService(PAYLOAD)
    .then((result: any) => {
      if (currentRequestId !== requestCounter) return;
      if (!result || !result.length) return;

      results.value[field as 'phone' | 'email'] = result;
      if (field === 'phone') {
        showPhoneCard.value = true;
      } else if (field === 'email') {
        showEmailCard.value = true;
      }
    })
    .finally(() => {
      if (currentRequestId !== requestCounter) return;
      isCheckingDuplicate.value = false;
    });
}

function setPersonData(field: string, data: PersonResult) {
  clearChecksAndPendingRequests();

  isSelectingFromModal = true;

  showPhoneCard.value = false;
  showEmailCard.value = false;

  contactData.value = data;
  personId.value = data._id.$oid;

  lastCheckedValue.value = '';

  const modalCloseTimeout = 50;
  setTimeout(() => {
    isSelectingFromModal = false;
  }, modalCloseTimeout);
}

function handleCloseModal() {
  showPhoneCard.value = false;
  showEmailCard.value = false;
  lastCheckedValue.value = '';
}
</script>

<template>
  <div>
    <AFieldPhone
      v-if="props.field.name === 'phone'"
      aria-describedby="person-help"
      v-model="model"
      :error
      :field
      :required
      :debounceTime="0"
      :inProgress="isCheckingDuplicate"
      @blur="handleBlur('phone')"
    />

    <AFieldEmail
      v-else-if="props.field.name === 'email'"
      aria-describedby="person-help"
      v-model="model"
      :error
      :field
      :required
      :debounceTime="0"
      :inProgress="isCheckingDuplicate"
      @blur="handleBlur('email')"
    />

    <Teleport to="body">
      <AFieldPersonCard
        v-if="showPhoneCard"
        :mode="field.settings.mode"
        :results="results.phone"
        @onClose="handleCloseModal"
        @onUsePerson="(data: any) => setPersonData(field.name, data)"
      />

      <AFieldPersonCard
        v-else-if="showEmailCard"
        :mode="field.settings.mode"
        :results="results.email"
        @onClose="handleCloseModal"
        @onUsePerson="(data: any) => setPersonData(field.name, data)"
      />
    </Teleport>
  </div>
</template>
